"use client";

import {
  ArrowRight,
  Clock3,
  MapPin,
  Menu,
  Package,
  Search,
  ShieldCheck,
  ShoppingCart,
  Star,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";

import StorefrontAdBanner from "@/components/store/StorefrontAdBanner";
import { dedicatedStoreTheme } from "@/components/store/dedicated-store-theme";
import { formatCurrency } from "@/lib/order";
import type { CartItem } from "@/types/cart";
import type { PublicStoreCampaign } from "@/types/public-campaign";
import type { PublicProduct, PublicStore } from "@/types/store";

import ServicesFooter from "./ServicesFooter";
import styles from "./services.module.css";

type Props = {
  store: PublicStore;
  primaryColor: string;
  instagramUrl: string;
  facebookUrl: string;
  formattedCnpj: string;
  attendanceLabel: string;
  contact: ReactNode;
  reviews: ReactNode;
  cart: CartItem[];
  cartItemsCount: number;
  showCart: boolean;
  onOpenProduct: (product: PublicProduct) => void;
  onAdd: (product: PublicProduct) => void;
  onOpenCart: () => void;
  onCampaignProducts: (ids: number[]) => void;
  onCampaignLead: (campaign: PublicStoreCampaign) => void;
  onVerification: () => void;
};

function ServiceCard({
  product,
  onOpen,
}: {
  product: PublicProduct;
  onOpen: () => void;
}) {
  const image =
    product.primary_image_url || product.images?.[0]?.image_url || null;

  const price = Number(product.current_price || product.price || 0);

  return (
    <article className={styles.serviceCard}>
      <button
        type="button"
        className={styles.serviceImage}
        onClick={onOpen}
        aria-label={`Ver detalhes de ${product.name}`}
      >
        {image ? (
          <img src={image} alt={product.name} loading="lazy" decoding="async" />
        ) : (
          <span className={styles.imageFallback}>
            <Wrench size={42} strokeWidth={1.3} />
          </span>
        )}
      </button>

      <div className={styles.serviceBody}>
        {product.category_name && (
          <p className={styles.cardEyebrow}>{product.category_name}</p>
        )}

        <h3>{product.name}</h3>

        {product.short_description && (
          <p className={styles.cardDescription}>{product.short_description}</p>
        )}

        <div className={styles.serviceBottom}>
          <div className={styles.servicePrice}>
            {price > 0 ? (
              <>
                <span>Valor</span>
                <strong>
                  {formatCurrency(product.current_price || product.price)}
                </strong>
              </>
            ) : (
              <>
                <span>Valor</span>
                <strong>Consulte</strong>
              </>
            )}
          </div>

          <button
            type="button"
            className={styles.detailsButton}
            onClick={onOpen}
          >
            Saiba mais
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductCard({
  product,
  quantity,
  onOpen,
  onAdd,
  onOpenCart,
}: {
  product: PublicProduct;
  quantity: number;
  onOpen: () => void;
  onAdd: () => void;
  onOpenCart: () => void;
}) {
  const image =
    product.primary_image_url || product.images?.[0]?.image_url || null;

  const hasPromotion =
    Boolean(product.promotional_price) &&
    Number(product.promotional_price) < Number(product.price);

  return (
    <article className={styles.productCard}>
      <button
        type="button"
        className={styles.productImage}
        onClick={onOpen}
        aria-label={`Ver detalhes de ${product.name}`}
      >
        {image ? (
          <img src={image} alt={product.name} loading="lazy" decoding="async" />
        ) : (
          <Package size={38} strokeWidth={1.2} />
        )}
      </button>

      <div className={styles.productBody}>
        <h3>{product.name}</h3>

        {product.reference_code && (
          <p className={styles.reference}>Ref. {product.reference_code}</p>
        )}

        <div className={styles.productPrice}>
          {hasPromotion && <del>{formatCurrency(product.price)}</del>}

          <strong>{formatCurrency(product.current_price)}</strong>
        </div>

        <button
          type="button"
          className={styles.cartAction}
          disabled={!product.is_available}
          onClick={quantity > 0 && !product.has_variations ? onOpenCart : onAdd}
        >
          {!product.is_available
            ? "Indisponível"
            : quantity > 0 && !product.has_variations
              ? `No carrinho (${quantity})`
              : product.has_variations || product.option_groups?.length
                ? "Escolher opções"
                : "Comprar"}
        </button>
      </div>
    </article>
  );
}

export default function ServicesStorefront({
  store,
  primaryColor,
  instagramUrl,
  facebookUrl,
  attendanceLabel,
  contact,
  reviews,
  cart,
  cartItemsCount,
  showCart,
  onOpenProduct,
  onAdd,
  onOpenCart,
  onCampaignProducts,
  onCampaignLead,
  onVerification,
}: Props) {
  const { company, categories } = store;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const services = useMemo(
    () => store.products.filter((product) => product.item_kind === "service"),
    [store.products],
  );

  const products = useMemo(
    () =>
      company.commerce_enabled
        ? store.products.filter((product) => product.item_kind === "product")
        : [],
    [company.commerce_enabled, store.products],
  );

  const featuredServices = useMemo(() => {
    const featured = services.filter((product) => product.is_featured);

    return (featured.length ? featured : services).slice(0, 6);
  }, [services]);

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);

  const serviceCategories = categories.filter((category) =>
    services.some((service) => service.category === category.id),
  );

  const filteredServices = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("pt-BR");

    return services.filter((service) => {
      const matchesCategory =
        categoryId === null || service.category === categoryId;

      const matchesSearch =
        !normalized ||
        [
          service.name,
          service.short_description,
          service.description,
          service.category_name,
          service.subcategory_name,
          service.reference_code,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("pt-BR")
          .includes(normalized);

      return matchesCategory && matchesSearch;
    });
  }, [services, query, categoryId]);

  const location = [company.city, company.state].filter(Boolean).join(", ");

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setQuery(search);
    scrollTo("todos-servicos");
  }

  function clearFilters() {
    setSearch("");
    setQuery("");
    setCategoryId(null);
  }

  function campaign(
    placement:
      "after_featured" | "between_most_viewed_and_categories" | "before_footer",
  ) {
    const current = store.campaigns?.banners[placement];

    return (
      <StorefrontAdBanner
        campaign={current}
        onProducts={onCampaignProducts}
        onLead={current ? () => onCampaignLead(current) : undefined}
        className={styles.campaign}
      />
    );
  }

  return (
    <div
      id="inicio"
      className={styles.storefront}
      data-storefront-variant="services"
      style={dedicatedStoreTheme(primaryColor, "#0f172a")}
    >

      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.brandGroup}>
            <button
              type="button"
              className={styles.menuToggle}
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="services-navigation"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              {mobileMenuOpen ? (
                <X size={22} aria-hidden="true" />
              ) : (
                <Menu size={22} aria-hidden="true" />
              )}
            </button>

            <a href="#inicio" className={styles.brand}>
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} />
            ) : (
              <span className={styles.brandIcon}>
                <Wrench size={24} />
              </span>
            )}

            <div className={styles.brandText}>
              <h1>{company.name}</h1>

              {location && (
                <span className={styles.location}>
                  <MapPin size={13} />
                  {location}
                </span>
              )}
            </div>
            </a>
          </div>

          <nav
            id="services-navigation"
            className={`${styles.navigation} ${
              mobileMenuOpen ? styles.navigationOpen : ""
            }`}
            aria-label="Navegação principal"
          >
            <a href="#inicio" onClick={() => setMobileMenuOpen(false)}>Início</a>
            <a href="#servicos" onClick={() => setMobileMenuOpen(false)}>Serviços</a>

            {products.length > 0 && <a href="#produtos" onClick={() => setMobileMenuOpen(false)}>Produtos</a>}

            {company.description && <a href="#sobre" onClick={() => setMobileMenuOpen(false)}>Sobre</a>}

            <a href="#avaliacoes" onClick={() => setMobileMenuOpen(false)}>Avaliações</a>
            <a href="#contato" onClick={() => setMobileMenuOpen(false)}>Contato</a>
          </nav>

          <div className={styles.headerActions}>
            {showCart && (
              <button
                type="button"
                className={styles.headerCart}
                onClick={onOpenCart}
                aria-label={`Abrir carrinho, ${cartItemsCount} itens`}
              >
                <span className={styles.headerCartIcon}>
                  <ShoppingCart size={22} aria-hidden="true" />
                  {cartItemsCount > 0 && <b>{cartItemsCount}</b>}
                </span>
                <span className={styles.headerCartLabel}>Meu carrinho</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {company.banner_url && (
        <section
          className={styles.hero}
          aria-label={`Banner de ${company.name}`}
        >
          <img
            src={company.banner_url}
            alt={`Banner de ${company.name}`}
            width={1920}
            height={570}
            fetchPriority="high"
            decoding="async"
          />
        </section>
      )}

      <main>
        <section
          id="servicos"
          className={`${styles.container} ${styles.servicesSection}`}
        >
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>Nossos serviços</p>

              <h2>Serviços principais</h2>

              <p>
                Conheça os principais serviços oferecidos pela {company.name}.
              </p>
            </div>

            {services.length > 6 && (
              <button
                type="button"
                className={styles.outlineButton}
                onClick={() => scrollTo("todos-servicos")}
              >
                Ver todos
                <ArrowRight size={16} />
              </button>
            )}
          </div>

          {featuredServices.length > 0 ? (
            <div className={styles.servicesGrid}>
              {featuredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  product={service}
                  onOpen={() => onOpenProduct(service)}
                />
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Wrench size={40} strokeWidth={1.2} />
              <h3>Serviços em breve</h3>
              <p>A empresa ainda não publicou serviços.</p>
            </div>
          )}
        </section>

        {campaign("after_featured")}

        <section className={styles.benefits}>
          <div className={styles.container}>
            <p className={styles.eyebrow}>Por que escolher {company.name}</p>

            <div className={styles.benefitsGrid}>
              <article>
                <ShieldCheck size={30} />
                <div>
                  <h3>Qualidade</h3>
                  <p>Atendimento profissional e cuidadoso.</p>
                </div>
              </article>

              <article>
                <Clock3 size={30} />
                <div>
                  <h3>Agilidade</h3>
                  <p>Resposta rápida e acompanhamento próximo.</p>
                </div>
              </article>

              <article>
                <Users size={30} />
                <div>
                  <h3>Especialistas</h3>
                  <p>Serviço feito por quem entende.</p>
                </div>
              </article>

              <article>
                <Star size={30} />
                <div>
                  <h3>Confiança</h3>
                  <p>Transparência do início ao fim.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {products.length > 0 && (
          <section
            id="produtos"
            className={`${styles.container} ${styles.productsSection}`}
          >
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>Produtos disponíveis</p>

                <h2>Produtos para comprar</h2>

                <p>
                  Além dos serviços, encontre produtos selecionados pela
                  empresa.
                </p>
              </div>
            </div>

            <div className={styles.productsGrid}>
              {products.map((product) => {
                const quantity = cart
                  .filter((item) => item.product.id === product.id)
                  .reduce((sum, item) => sum + item.quantity, 0);

                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    quantity={quantity}
                    onOpen={() => onOpenProduct(product)}
                    onAdd={() => onAdd(product)}
                    onOpenCart={onOpenCart}
                  />
                );
              })}
            </div>
          </section>
        )}

        {campaign("between_most_viewed_and_categories")}

        <section
          id="todos-servicos"
          className={`${styles.container} ${styles.catalogSection}`}
        >
          <div className={styles.catalogHeader}>
            <div>
              <p className={styles.eyebrow}>Encontre o que precisa</p>

              <h2>Todos os serviços</h2>
            </div>

            <form
              className={styles.search}
              role="search"
              onSubmit={submitSearch}
            >
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar serviço..."
                aria-label="Buscar serviço"
              />

              <button type="submit" aria-label="Buscar">
                <Search size={19} />
              </button>
            </form>
          </div>

          {serviceCategories.length > 0 && (
            <div className={styles.filters}>
              <button
                type="button"
                aria-pressed={categoryId === null}
                onClick={() => {
                  setCategoryId(null);
                  setQuery("");
                  setSearch("");
                }}
              >
                Todos
              </button>

              {serviceCategories.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  aria-pressed={categoryId === category.id}
                  onClick={() => {
                    setCategoryId(category.id);
                    setQuery("");
                    setSearch("");
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}

          {(query || categoryId !== null) && (
            <div className={styles.resultInfo}>
              <span>
                {filteredServices.length}{" "}
                {filteredServices.length === 1
                  ? "serviço encontrado"
                  : "serviços encontrados"}
              </span>

              <button type="button" onClick={clearFilters}>
                <X size={15} />
                Limpar filtros
              </button>
            </div>
          )}

          {filteredServices.length > 0 ? (
            <div className={styles.servicesGrid}>
              {filteredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  product={service}
                  onOpen={() => onOpenProduct(service)}
                />
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Search size={38} strokeWidth={1.2} />
              <h3>Nenhum serviço encontrado</h3>
              <p>Tente outro termo ou remova os filtros.</p>
            </div>
          )}
        </section>

        {company.description && (
          <section id="sobre" className={styles.about}>
            <div className={`${styles.container} ${styles.aboutGrid}`}>
              <div className={styles.aboutVisual}>
                {company.banner_url ? (
                  <img
                    src={company.banner_url}
                    alt={company.name}
                    loading="lazy"
                    decoding="async"
                  />
                ) : company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company.name}
                    loading="lazy"
                    decoding="async"
                    className={styles.aboutLogo}
                  />
                ) : (
                  <Wrench size={80} strokeWidth={1} />
                )}
              </div>

              <div className={styles.aboutCopy}>
                <p className={styles.eyebrow}>Sobre nós</p>

                <h2>Sobre {company.name}</h2>

                <p>{company.description}</p>

                <div className={styles.aboutInfo}>
                  {location && (
                    <span>
                      <MapPin size={18} />
                      {location}
                    </span>
                  )}

                  {attendanceLabel && (
                    <span>
                      <Clock3 size={18} />
                      {attendanceLabel}
                    </span>
                  )}

                  {company.is_verified && (
                    <button type="button" onClick={onVerification}>
                      <ShieldCheck size={18} />
                      Empresa verificada
                    </button>
                  )}
                </div>
                {contact && <div className={styles.contact}>{contact}</div>}
              </div>
            </div>
          </section>
        )}

        <section id="avaliacoes" className={styles.reviews}>
          {reviews}
        </section>

        {campaign("before_footer")}
      </main>

      <div id="contato">
        <ServicesFooter
          company={company}
          primaryColor={primaryColor}
          instagramUrl={instagramUrl}
          facebookUrl={facebookUrl}
          hasProducts={products.length > 0}
          hasAbout={Boolean(company.description)}
        />
      </div>
    </div>
  );
}
